import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MeView } from '@gc/shared';
import { api } from './lib/api.js';
import { openTarget } from './lib/telegram.js';
import { Today } from './screens/Today.js';
import { Plan } from './screens/Plan.js';
import { Trainers } from './screens/Trainers.js';
import { Profile } from './screens/Profile.js';
import { Admin } from './screens/Admin.js';

type Screen = 'today' | 'plan' | 'trainers' | 'profile' | 'admin';

interface Target {
  kind: string;
  id: string;
}

/**
 * Куда открыть приложение. Кнопки бота передают строку вида chord_Em,
 * tab_ode, strum_eight, day_12 или admin — разбираем её здесь, чтобы
 * ребёнок попадал сразу в нужный тренажёр, а не искал его по вкладкам.
 */
function parseTarget(raw: string): { screen: Screen; target?: Target } {
  if (!raw) return { screen: 'today' };
  if (raw === 'admin') return { screen: 'admin' };
  if (raw.startsWith('day_')) return { screen: 'today' };

  const [kind, ...rest] = raw.split('_');
  const id = rest.join('_');
  if (kind === 'tuner') return { screen: 'trainers', target: { kind: 'tuner', id: 'tuner' } };
  if (kind === 'chord' || kind === 'tab' || kind === 'strum') {
    return { screen: 'trainers', target: { kind, id } };
  }
  return { screen: 'today' };
}

export function App(): JSX.Element {
  const initial = useMemo(() => parseTarget(openTarget()), []);
  const [screen, setScreen] = useState<Screen>(initial.screen);
  const [target, setTarget] = useState<Target | undefined>(initial.target);
  const [me, setMe] = useState<MeView | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        setMe(await api.me());
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Не удалось загрузить прогресс');
      }
    })();
  }, []);

  const toggle = useCallback(async (day: number, done: boolean) => {
    setBusy(true);
    try {
      const res = await api.complete(day, done);
      setMe((prev) => (prev ? { ...prev, progress: res.progress } : prev));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить отметку');
    } finally {
      setBusy(false);
    }
  }, []);

  const openTool = useCallback((kind: string, id: string) => {
    setTarget({ kind, id });
    setScreen('trainers');
  }, []);

  if (error && !me) {
    return (
      <div className="screen">
        <div className="card center">
          <h2>Не открылось</h2>
          <p className="hint">{error}</p>
          <p className="hint">Это приложение работает внутри Telegram — откройте его кнопкой из бота.</p>
        </div>
      </div>
    );
  }

  if (!me) return <div className="screen"><p className="hint">Загружаю…</p></div>;

  const tabs: [Screen, string][] = [
    ['today', 'Сегодня'],
    ['plan', 'План'],
    ['trainers', 'Тренажёры'],
    ['profile', 'Профиль'],
  ];
  if (me.isAdmin) tabs.push(['admin', 'Статистика']);

  return (
    <div className="app">
      <main>
        {screen === 'today' && (
          <Today progress={me.progress} busy={busy} onToggle={toggle} onTool={openTool} />
        )}
        {screen === 'plan' && (
          <Plan progress={me.progress} busy={busy} onToggle={toggle} onTool={openTool} />
        )}
        {screen === 'trainers' && <Trainers target={target} />}
        {screen === 'profile' && (
          <Profile
            me={me}
            onSettings={(reminderHour) =>
              setMe((prev) => (prev ? { ...prev, settings: { reminderHour } } : prev))
            }
          />
        )}
        {screen === 'admin' && me.isAdmin && <Admin />}
      </main>

      <nav className="tabbar">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            type="button"
            aria-pressed={screen === key}
            onClick={() => setScreen(key)}
          >
            {label}
          </button>
        ))}
      </nav>
    </div>
  );
}
